import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PapiDeviation } from '@wix/da-papi-types';
import { getEnv } from '@wix/da-react-app/pkg/client/Environment/redux/selectors';
import { getPageSize } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { actions as streamActions, StreamType } from '@wix/da-shared-react/pkg/Stream';
import { getStreamIdsByGroupId } from '@wix/da-shared-react/pkg/Stream/base/selectors';
import { useNormalizedDeviationStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import {
  getGallectionSectionType,
  getSearchQuery,
  getShouldHideFolderSlider,
  getSortOrder,
} from '../../../../../selectors/gallectionSection';
import { getCurrentPage } from '../../../../../selectors/pageInfo';
import {
  GALLECTION_SEARCH_STREAM_GROUP_ID,
  getGallectionSearchStreamId,
} from '../../../../../streams/folderDeviations';
import { fetchNextGallectionSearchResults } from '../../../../../actions/gallectionSection';
import { GallectionType } from '../../../../../../types/gallection';

export interface UsePagedGallectionSearchReturn {
  currentPage: number;
  deviations: PapiDeviation[];
  errorMessage?: string;
  gallectionType: GallectionType;
  hideFolderSlider: boolean;
  isLoadingFirstResults: boolean;
  requestUrl: string;
  searchQuery: string;
  streamHasMore: boolean;
  streamId: string;
  streamTotalCount: number;
  totalPages: number;
}

export default function usePagedGallectionSearch(): UsePagedGallectionSearchReturn {
  const dispatch = useDispatch();
  const searchQuery = useSelector(getSearchQuery);
  const sortOrder = useSelector(getSortOrder);
  const gallectionType = useSelector(getGallectionSectionType);
  const currentPage = useSelector(getCurrentPage);
  const pageSize = useSelector(getPageSize);
  const hideFolderSlider = useSelector(getShouldHideFolderSlider);
  const requestUrl = useSelector(state => getEnv(state).requestUrl) ?? '';
  const streamId = useMemo(
    () =>
      getGallectionSearchStreamId({
        gallectionType,
        mode: 'paged',
        page: currentPage,
        searchQuery,
        sortOrder,
      }),
    [currentPage, gallectionType, searchQuery, sortOrder]
  );
  const streamIds = useSelector(state =>
    getStreamIdsByGroupId(state, GALLECTION_SEARCH_STREAM_GROUP_ID)
  );
  const expectedOffset = useMemo(
    () => Math.max(0, (currentPage - 1) * pageSize),
    [currentPage, pageSize]
  );
  const initConfig = useMemo(
    () => ({
      autoFetch: true,
      fetchNextCallback: fetchNextGallectionSearchResults,
      groupId: GALLECTION_SEARCH_STREAM_GROUP_ID,
      hasMore: true,
      itemsPerFetch: pageSize,
      nextOffset: expectedOffset,
      streamParams: {
        gallectionType,
        mode: 'paged' as const,
        searchQuery,
        sortOrder,
      },
      streamType: StreamType.WITH_OFFSET,
    }),
    [expectedOffset, gallectionType, pageSize, searchQuery, sortOrder]
  );
  const stream = useNormalizedDeviationStream(streamId, 500, initConfig);

  useEffect(() => {
    streamIds
      .filter(staleStreamId => staleStreamId !== streamId)
      .forEach(staleStreamId => dispatch(streamActions.deleteStream(staleStreamId)));
  }, [dispatch, streamId, streamIds]);

  const totalPages = stream.total ? Math.max(1, Math.ceil(stream.total / pageSize)) : currentPage;

  return {
    currentPage,
    deviations: stream.items,
    errorMessage: stream.errorMessage,
    gallectionType,
    hideFolderSlider,
    isLoadingFirstResults: !stream.items.length && (!stream.isInitialized || stream.isFetching),
    requestUrl,
    searchQuery,
    streamHasMore: stream.hasMore,
    streamId,
    streamTotalCount: stream.total || 0,
    totalPages: stream.hasMore ? Math.max(totalPages, currentPage + 1) : totalPages,
  };
}
